import { IndustryOption } from './types';

// Alphabetized industry list.
export const INDUSTRY_OPTIONS = [
  {
    code: 'AGRICULTURE',
    label: 'Agriculture',
    keywords: ['farm', 'farming', 'ranch', 'crops']
  },
  {
    code: 'AUTOMOTIVE',
    label: 'Automotive',
    keywords: ['auto', 'car', 'dealership', 'mechanic']
  },
  {
    code: 'CHILDCARE',
    label: 'Childcare',
    keywords: ['daycare', 'babysitting', 'nanny']
  },
  {
    code: 'CONSTRUCTION',
    label: 'Construction',
    keywords: ['contractor', 'builder', 'remodeling']
  },
  {
    code: 'CONSULTING',
    label: 'Consulting',
    keywords: ['advisor', 'consultant']
  },
  {
    code: 'CREATIVE_SERVICES',
    label: 'Creative Services',
    keywords: ['design', 'photography', 'marketing', 'video']
  },
  {
    code: 'DIRECT_SALES',
    label: 'Direct Sales',
    keywords: ['mlm', 'network marketing']
  },
  {
    code: 'EDUCATION',
    label: 'Education',
    keywords: ['school', 'teacher', 'tutoring']
  },
  {
    code: 'ENERGY',
    label: 'Energy',
    keywords: ['solar', 'oil', 'gas', 'utilities']
  },
  {
    code: 'ENTERTAINMENT',
    label: 'Entertainment',
    keywords: ['music', 'film', 'media', 'events']
  },
  {
    code: 'FINANCE',
    label: 'Finance',
    keywords: ['accounting', 'bookkeeping', 'financial planning']
  },
  {
    code: 'FITNESS',
    label: 'Fitness',
    keywords: ['gym', 'personal trainer', 'yoga']
  },
  {
    code: 'HEALTHCARE',
    label: 'Healthcare',
    keywords: ['medical', 'doctor', 'dentist', 'clinic']
  },
  {
    code: 'HOME_SERVICES',
    label: 'Home Services',
    keywords: ['cleaning', 'landscaping', 'hvac', 'plumbing']
  },
  {
    code: 'HOSPITALITY',
    label: 'Hospitality',
    keywords: ['hotel', 'motel', 'lodging']
  },
  {
    code: 'INSURANCE',
    label: 'Insurance',
    keywords: ['agent', 'broker', 'policy']
  },
  {
    code: 'LEGAL',
    label: 'Legal',
    keywords: ['lawyer', 'attorney', 'law firm']
  },
  {
    code: 'MANUFACTURING',
    label: 'Manufacturing',
    keywords: ['factory', 'production', 'industrial']
  },
  {
    code: 'NONPROFIT',
    label: 'Nonprofit',
    keywords: ['charity', 'ngo']
  },
  {
    code: 'ONLINE_SELLING',
    label: 'Online Selling',
    keywords: ['ecommerce', 'amazon', 'etsy', 'dropshipping']
  },
  {
    code: 'PERSONAL_CARE',
    label: 'Personal Care',
    keywords: ['salon', 'spa', 'barber', 'beauty']
  },
  {
    code: 'PET_SERVICES',
    label: 'Pet Services',
    keywords: ['grooming', 'veterinary', 'boarding', 'dog walking']
  },
  {
    code: 'PROFESSIONAL_SERVICES',
    label: 'Professional Services',
    keywords: ['bookkeeping', 'administrative', 'services']
  },
  {
    code: 'REAL_ESTATE_SALES',
    label: 'Real Estate Sales',
    keywords: ['realtor', 'agent', 'broker']
  },
  {
    code: 'RELIGIOUS_ORGANIZATION',
    label: 'Religious Organization',
    keywords: ['church', 'ministry', 'pastor', 'clergy']
  },
  {
    code: 'RENTAL_PROPERTY',
    label: 'Rental Property',
    keywords: ['landlord', 'rental', 'leasing', 'airbnb']
  },
  {
    code: 'RESTAURANT',
    label: 'Restaurant',
    keywords: ['dining', 'catering', 'food service', 'cafe']
  },
  {
    code: 'RETAIL',
    label: 'Retail',
    keywords: ['store', 'shop', 'boutique']
  },
  {
    code: 'STAFFING',
    label: 'Staffing',
    keywords: ['recruiting', 'temp agency', 'hr']
  },
  {
    code: 'TECHNOLOGY',
    label: 'Technology',
    keywords: ['tech', 'software', 'it', 'saas']
  },
  {
    code: 'TRANSPORTATION',
    label: 'Transportation',
    keywords: ['shipping', 'logistics', 'delivery', 'courier']
  },
  {
    code: 'TRUCKING',
    label: 'Trucking',
    keywords: ['freight', 'hauling', 'trucker']
  },
  {
    code: 'WHOLESALE',
    label: 'Wholesale',
    keywords: ['distributor', 'bulk', 'supplier']
  }
] satisfies IndustryOption[];

// Literal union of the codes above - keeps INDUSTRY_NICHE_CODE_BY_OPTION's keys checked, so a
// typo'd/renamed code fails to compile instead of silently mapping nothing.
type IndustryCode = (typeof INDUSTRY_OPTIONS)[number]['code'];

// Sentinel for the always-last "Other" row - not a real industry, so it stays out of
// INDUSTRY_OPTIONS and never participates in filterIndustries' matching.
export const OTHER_INDUSTRY_OPTION: IndustryOption = {
  code: 'OTHER',
  label: 'Other'
};

// Maps this list's codes to the real `industry_niche` AI-tag vocabulary the backend already
// knows about. Best-effort, not exhaustive - an option with no reasonable backend counterpart
// is left out entirely, and a missing entry means no filter, same as an empty termValues
// elsewhere. CONSULTING/FINANCE/INSURANCE -> PROFESSIONAL_SERVICES and ENERGY -> OIL_GAS are
// the weakest links here - flagged for a second look, not confident mappings like the rest.
export const INDUSTRY_NICHE_CODE_BY_OPTION: Partial<
  Record<IndustryCode, string>
> = {
  AGRICULTURE: 'FARM_AG',
  AUTOMOTIVE: 'AUTOMOTIVE',
  CONSTRUCTION: 'CONSTRUCTION',
  CONSULTING: 'PROFESSIONAL_SERVICES',
  ENERGY: 'OIL_GAS',
  ENTERTAINMENT: 'ENTERTAINMENT',
  FINANCE: 'PROFESSIONAL_SERVICES',
  HEALTHCARE: 'MEDICAL_DENTAL',
  HOSPITALITY: 'HOSPITALITY',
  INSURANCE: 'PROFESSIONAL_SERVICES',
  LEGAL: 'LEGAL',
  MANUFACTURING: 'MANUFACTURING',
  NONPROFIT: 'NONPROFIT',
  ONLINE_SELLING: 'ECOMMERCE_RETAIL',
  PERSONAL_CARE: 'SALON_PERSONAL_CARE',
  PROFESSIONAL_SERVICES: 'PROFESSIONAL_SERVICES',
  REAL_ESTATE_SALES: 'REAL_ESTATE',
  RELIGIOUS_ORGANIZATION: 'CLERGY_RELIGIOUS',
  RENTAL_PROPERTY: 'REAL_ESTATE',
  RESTAURANT: 'RESTAURANT_FOOD_SERVICE',
  RETAIL: 'ECOMMERCE_RETAIL',
  TECHNOLOGY: 'TECHNOLOGY',
  TRANSPORTATION: 'TRUCKING_TRANSPORT',
  TRUCKING: 'TRUCKING_TRANSPORT'
};
